import axios from 'axios';
import { memoize } from 'lodash-es';
import {
  Platform,
  fallbackToConsumerToken,
  payments,
  tourflow,
  shop,
} from '@aviata/platform-client';
import { createStorage, type StorageWrapper } from '@aviata/storage';

import { getCfTurnstileChallenge } from '@shared/lib/cf-turnstile';
import { env } from '../config';
import { apiLib } from './lib';

const TIMEOUT = 120_000;
/**
 * @description Consumer token localStorage key.
 * It is Shared between all Aviata apps, so
 * keep it in sync with other apps
 */
export const CONSUMER_TOKEN_KEY = 'token';

function getConsumerTokenFromStorage(storage: StorageWrapper) {
  const consumerToken = storage.get(CONSUMER_TOKEN_KEY);

  return typeof consumerToken === 'string' ? consumerToken : '';
}

function onConsumerTokenUpdate(token: string) {
  const storage = createStorage(localStorage);
  storage.set(CONSUMER_TOKEN_KEY, token);
}

export function createPfmInstance() {
  if (env.IS_SSR) {
    /**
     * @description Indentation is correct here, because we want to have no left indentation on every line
     */
    throw new Error(`Client PFM is initiated on the server
    
You see this error because this PFM client should only be used on the client side.
If you need to fetch data on the server side for SEO or UX purposes, you should use the server side PFM client
and pass the data to the client side as server side props in "getServerSideProps" method.
@see https://vite-plugin-ssr.com/passToClient
    
Otherwise you may use "if (!env.IS_SSR) { ... }" or wrap component in <ClientOnly> to prevent this error`);
  }

  const storage = createStorage(localStorage);
  const consumerToken = getConsumerTokenFromStorage(storage);
  const { name, key } = apiLib.getConsumerTokenCredentials();

  const pfm = Platform(
    axios,
    {
      baseURL: env.PFM_API_URL,
      headers: {
        'accept-language': apiLib.getAcceptLanguageByLocale(),
      },
      consumer: {
        name,
        key,
        token: consumerToken,
      },
      initStrategy: fallbackToConsumerToken,
      timeout: TIMEOUT,
    },
    {
      payments,
      tourflow,
      shop,
    },
  );

  getCfTurnstileChallenge()?.withAxiosInterceptors(pfm.client);

  pfm.emitter.on('consumer-token-update', onConsumerTokenUpdate);

  return pfm;
}

/**
 * @description Memoized PFM instance creation to use a single instance
 *
 * Use memoize instead of once because once caches PFM init errors
 * and the app does not log them on retry
 *
 * Memoize calls a function multiple times it it throws an error,
 * so we can log the initial error
 */
export const getPfmInstance = memoize(createPfmInstance);
export {
  poll,
  POLLING_MAX_ATTEMPTS_ERROR,
  type PollingFetcher,
} from './polling';
export { apiLib } from './lib';
